import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { STATUS_LABELS, STATUS_CLASSES, formatMinutes } from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
  PieChart, Pie,
} from "recharts";
import { Timer, ClipboardList, CheckCircle2, AlertTriangle, PlusCircle, Sparkles, PackageX } from "lucide-react";

const STATUS_HEX = {
  in_attesa: "#3B82F6",
  in_corso: "#F59E0B",
  completato: "#10B981",
  scarto_bloccato: "#F43F5E",
};

function KpiCard({ icon: Icon, label, value, unit, testid, accent }) {
  return (
    <div
      className="rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6 shadow-lg shadow-black/40 hover:bg-slate-800/50 transition-colors"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono-data uppercase tracking-widest text-slate-400">{label}</span>
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <div className="mt-4 flex items-baseline gap-1.5">
        <span className="font-mono-data font-bold text-3xl tracking-wider" style={{ color: accent }}>
          {value}
        </span>
        {unit && <span className="text-sm text-slate-500">{unit}</span>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    const [s, a] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/assemblies"),
    ]);
    setStats(s.data);
    setRecent(a.data.slice(0, 6));
  };

  useEffect(() => {
    load();
  }, []);

  if (!stats) {
    return <div className="text-slate-500">Caricamento…</div>;
  }

  const pieData = stats.status_data.filter((d) => d.value > 0);

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Dashboard Produzione</h1>
          <p className="text-slate-500 text-sm mt-1">Panoramica tempi, commesse e stato dei montaggi.</p>
        </div>
        <button
          onClick={() => navigate("/nuovo")}
          data-testid="btn-new-assembly"
          className="h-10 px-5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center gap-2 transition-colors"
        >
          <PlusCircle className="h-4 w-4" /> Nuovo Montaggio
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <KpiCard icon={Timer} label="Tempo medio" value={formatMinutes(stats.avg_time)} testid="kpi-avg-time" accent="#F59E0B" />
        <KpiCard icon={ClipboardList} label="Commesse attive" value={stats.active_orders} testid="kpi-active-orders" accent="#2563EB" />
        <KpiCard icon={CheckCircle2} label="Completamento" value={stats.completion_rate} unit="%" testid="kpi-completion" accent="#10B981" />
        <KpiCard icon={AlertTriangle} label="Scarti / bloccati" value={stats.scarti} testid="kpi-scarti" accent="#F43F5E" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar per line */}
        <div className="lg:col-span-2 rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
          <h2 className="font-heading text-lg font-bold mb-6">Montaggi per linea</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.per_line}>
              <XAxis dataKey="name" stroke="#64748B" tick={{ fontSize: 11 }} />
              <YAxis stroke="#64748B" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: "#1F2937" }}
                contentStyle={{ background: "#111827", border: "1px solid #334155", borderRadius: 8, color: "#F8FAFC" }}
              />
              <Bar dataKey="montaggi" radius={[6, 6, 0, 0]}>
                {stats.per_line.map((_, i) => (
                  <Cell key={i} fill="#F59E0B" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie status */}
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
          <h2 className="font-heading text-lg font-bold mb-2">Stato montaggi</h2>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3}>
                {pieData.map((d, i) => (
                  <Cell key={i} fill={STATUS_HEX[d.name]} stroke="none" />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: "#111827", border: "1px solid #334155", borderRadius: 8 }}
                formatter={(v, n) => [v, STATUS_LABELS[n]]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-3">
            {stats.status_data.map((s) => (
              <div key={s.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-400">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_HEX[s.name] }} />
                  {STATUS_LABELS[s.name]}
                </span>
                <span className="font-mono-data text-slate-200">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Low stock */}
      {stats.low_stock_items && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6" data-testid="dashboard-low-stock">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-lg font-bold flex items-center gap-2">
              <PackageX className="h-4 w-4 text-rose-400" /> Componenti sotto scorta
            </h2>
            <button onClick={() => navigate("/magazzino")} className="text-sm text-amber-400 hover:text-amber-300">
              Vai al magazzino →
            </button>
          </div>
          {stats.low_stock_items.length === 0 ? (
            <p className="text-slate-500 text-sm">Tutte le giacenze sono sopra la scorta minima. 👍</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {stats.low_stock_items.map((w) => (
                <div key={w.id} className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
                  <div className="text-sm font-medium truncate">{w.name}</div>
                  <div className="text-xs text-slate-500 font-mono-data">{w.codice_prodotto} · {w.ubicazione}</div>
                  <div className="text-xs font-mono-data mt-1.5 text-rose-400">Giacenza {w.quantity} / min {w.reorder_threshold}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-400" /> Ultime lavorazioni
          </h2>
          <button onClick={() => navigate("/storico")} className="text-sm text-amber-400 hover:text-amber-300">
            Vedi tutto →
          </button>
        </div>
        <div className="space-y-2">
          {recent.length === 0 && <p className="text-slate-500 text-sm">Nessun montaggio registrato.</p>}
          {recent.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 py-3 border-b border-slate-800/70 last:border-0">
              <span className="font-mono-data text-sm text-amber-400 w-32">{r.commessa}</span>
              <span className="text-sm text-slate-200 flex-1 min-w-[140px]">{r.part_name}</span>
              <span className="text-xs text-slate-500 hidden sm:block">{r.line_name}</span>
              <span className="text-xs font-mono-data text-slate-400 w-16 text-right">{formatMinutes(r.time_minutes)}</span>
              <span className={`text-[11px] px-2.5 py-1 rounded-full border ${STATUS_CLASSES[r.status]}`}>
                {STATUS_LABELS[r.status]}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
